import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";

import {
   getMessageTemplates,
   resetMessageTemplate,
   updateMessageTemplate,
} from "../../api/messageTemplatesService";
import {
   exampleVars,
   placeholdersIn,
   renderMessageText,
} from "../../utils/messageTemplatePreview";

const CHANNELS = [
   {
      id: "chat",
      label: "Chat",
      hint:
         "Mementourile de programare trimise zilnic, la ora 10:00, în chatul elevului cu managerul. Elevul le vede ca mesaje de la manager.",
   },
   {
      id: "notification",
      label: "Notificări",
      hint:
         "Notificările din aplicație (clopoțelul elevului): titlul și textul fiecăreia.",
   },
   {
      id: "email",
      label: "Email",
      hint:
         "Textele emailurilor. Aspectul (culori, butoane, atașamente) rămâne același; un rând gol desparte paragrafele, rândurile care încep cu „- ” devin listă, cele cu „1. ” listă numerotată.",
   },
];

function formatDate(value) {
   if (!value) return "";
   try {
      return new Date(value).toLocaleString("ro-RO", {
         day: "2-digit",
         month: "2-digit",
         year: "numeric",
         hour: "2-digit",
         minute: "2-digit",
      });
   } catch {
      return "";
   }
}

/** Ce e greșit într-un text, după aceleași reguli ca la salvarea pe server. */
function validationError(template, value) {
   const text = String(value ?? "").trim();
   if (!text) return "Textul nu poate fi gol.";
   if (text.length > template.maxLength) {
      return `Textul poate avea cel mult ${template.maxLength} de caractere.`;
   }
   if (template.kind === "url") {
      return /^https:\/\/[^\s<>"']+$/.test(text)
         ? ""
         : "Linkul trebuie să fie o adresă completă care începe cu https://";
   }
   const allowed = new Set((template.placeholders || []).map((p) => p.name));
   const unknown = placeholdersIn(text).filter((name) => !allowed.has(name));
   return unknown.length
      ? `Eticheta {${unknown[0]}} nu e cunoscută pentru acest text.`
      : "";
}

/**
 * Textele trimise automat (mementouri în chat, notificări din aplicație,
 * emailuri). Adminul le poate schimba sau poate reveni la textul implicit.
 */
export default function APMessageTemplates() {
   const [data, setData] = useState(null);
   const [loadError, setLoadError] = useState("");
   const [channel, setChannel] = useState("chat");
   const [drafts, setDrafts] = useState({});
   const [busyKey, setBusyKey] = useState(null);
   const [feedback, setFeedback] = useState({});
   const inputRefs = useRef({});

   useEffect(() => {
      document.title = "Instruire Auto | Mesaje automate";
   }, []);

   const load = useCallback(() => {
      setLoadError("");
      getMessageTemplates()
         .then(setData)
         .catch((err) => setLoadError(err?.message || "Nu am putut încărca textele."));
   }, []);

   useEffect(() => {
      load();
   }, [load]);

   const counts = useMemo(() => {
      const out = {};
      for (const t of data?.templates || []) {
         out[t.channel] = (out[t.channel] || 0) + 1;
      }
      return out;
   }, [data]);

   const groups = useMemo(() => {
      const out = [];
      for (const t of data?.templates || []) {
         if (t.channel !== channel) continue;
         let g = out.find((x) => x.name === t.group);
         if (!g) {
            g = { name: t.group, items: [] };
            out.push(g);
         }
         g.items.push(t);
      }
      return out;
   }, [data, channel]);

   const activeChannel = CHANNELS.find((c) => c.id === channel) || CHANNELS[0];

   const replaceTemplate = (updated) => {
      if (!updated) return;
      setData((cur) =>
         cur
            ? { ...cur, templates: cur.templates.map((t) => (t.key === updated.key ? updated : t)) }
            : cur,
      );
      setDrafts((cur) => {
         const next = { ...cur };
         delete next[updated.key];
         return next;
      });
   };

   const setDraft = (key, value) => setDrafts((cur) => ({ ...cur, [key]: value }));

   // eticheta se pune acolo unde e cursorul (sau la final)
   const insertPlaceholder = (template, name) => {
      const current = drafts[template.key] ?? template.body;
      const input = inputRefs.current[template.key];
      const token = `{${name}}`;
      const start = input?.selectionStart ?? current.length;
      const end = input?.selectionEnd ?? current.length;
      const next = current.slice(0, start) + token + current.slice(end);
      setDraft(template.key, next);
      requestAnimationFrame(() => {
         if (!input) return;
         input.focus();
         input.setSelectionRange(start + token.length, start + token.length);
      });
   };

   const save = async (template) => {
      const body = drafts[template.key];
      setBusyKey(template.key);
      setFeedback((f) => ({ ...f, [template.key]: null }));
      try {
         replaceTemplate(await updateMessageTemplate(template.key, body));
         setFeedback((f) => ({ ...f, [template.key]: { ok: true, text: "Salvat." } }));
      } catch (err) {
         setFeedback((f) => ({ ...f, [template.key]: { ok: false, text: err?.message } }));
      } finally {
         setBusyKey(null);
      }
   };

   const reset = async (template) => {
      if (!window.confirm("Revii la textul implicit? Textul modificat se pierde.")) return;
      setBusyKey(template.key);
      setFeedback((f) => ({ ...f, [template.key]: null }));
      try {
         replaceTemplate(await resetMessageTemplate(template.key));
         setFeedback((f) => ({
            ...f,
            [template.key]: { ok: true, text: "S-a revenit la textul implicit." },
         }));
      } catch (err) {
         setFeedback((f) => ({ ...f, [template.key]: { ok: false, text: err?.message } }));
      } finally {
         setBusyKey(null);
      }
   };

   return (
      <main className="main">
         <section className="adminOther messageTemplates">
            <div className="adminOther__hero">
               <div className="adminOther__heroText">
                  <p className="adminOther__eyebrow">
                     <NavLink to="/admin/altele" className="messageTemplates__back">
                        Instrumente
                     </NavLink>{" "}
                     / Admin
                  </p>
                  <h1 className="adminOther__title">Mesaje automate</h1>
                  <p className="adminOther__subtitle">
                     Textele trimise automat elevilor. Etichetele dintre acolade, de
                     exemplu <code>{"{prenume}"}</code>, se înlocuiesc la trimitere;
                     fiecare text are etichetele lui, afișate sub câmp.
                  </p>
               </div>
            </div>

            {data && (
               <div className="messageTemplates__tabs" role="tablist">
                  {CHANNELS.map((c) => (
                     <button
                        key={c.id}
                        type="button"
                        role="tab"
                        aria-selected={channel === c.id}
                        className={`messageTemplates__tab ${channel === c.id ? "is-active" : ""}`}
                        onClick={() => setChannel(c.id)}
                     >
                        {c.label}
                        <span className="messageTemplates__tabCount">{counts[c.id] || 0}</span>
                     </button>
                  ))}
               </div>
            )}

            {data && <p className="messageTemplates__muted messageTemplates__hint">{activeChannel.hint}</p>}

            {loadError && (
               <section className="adminOther__section">
                  <p className="messageTemplates__error">{loadError}</p>
                  <button type="button" className="messageTemplates__btn" onClick={load}>
                     Încearcă din nou
                  </button>
               </section>
            )}

            {!data && !loadError && (
               <section className="adminOther__section">
                  <p className="messageTemplates__muted">Se încarcă textele...</p>
               </section>
            )}

            {groups.map((group) => (
               <section key={group.name} className="adminOther__section">
                  <div className="adminOther__sectionHead">
                     <h2>{group.name}</h2>
                  </div>

                  <div className="messageTemplates__list">
                     {group.items.map((t) => {
                        const draft = drafts[t.key];
                        const value = draft ?? t.body;
                        const dirty = draft !== undefined && draft !== t.body;
                        const error = validationError(t, value);
                        const busy = busyKey === t.key;
                        const fb = feedback[t.key];
                        const placeholders = t.placeholders || [];
                        const preview =
                           t.kind === "url" ? value : renderMessageText(value, exampleVars(placeholders));
                        const inputProps = {
                           ref: (el) => {
                              inputRefs.current[t.key] = el;
                           },
                           value,
                           disabled: busy,
                           onChange: (e) => setDraft(t.key, e.target.value),
                        };

                        return (
                           <article key={t.key} className="messageTemplates__card">
                              <header className="messageTemplates__cardHead">
                                 <div>
                                    <h3 className="messageTemplates__cardTitle">{t.title}</h3>
                                    <p className="messageTemplates__muted">{t.description}</p>
                                 </div>
                                 <span
                                    className={`messageTemplates__badge ${t.isCustom ? "is-custom" : ""}`}
                                    title={
                                       t.isCustom && t.updatedAt
                                          ? `Modificat ${formatDate(t.updatedAt)}`
                                          : undefined
                                    }
                                 >
                                    {t.isCustom ? "Modificat" : "Implicit"}
                                 </span>
                              </header>

                              {t.multiline ? (
                                 <textarea
                                    className="messageTemplates__textarea"
                                    rows={t.channel === "email" ? 6 : 4}
                                    {...inputProps}
                                 />
                              ) : (
                                 <input
                                    className="messageTemplates__input"
                                    type={t.kind === "url" ? "url" : "text"}
                                    {...inputProps}
                                 />
                              )}

                              {placeholders.length > 0 && (
                                 <div className="messageTemplates__chips">
                                    {placeholders.map((p) => (
                                       <button
                                          key={p.name}
                                          type="button"
                                          className="messageTemplates__chip"
                                          title={`Pune ${p.label} (ex.: ${p.example})`}
                                          disabled={busy}
                                          onClick={() => insertPlaceholder(t, p.name)}
                                       >
                                          {`{${p.name}}`}
                                       </button>
                                    ))}
                                 </div>
                              )}

                              <div className="messageTemplates__meta">
                                 <span className={value.length > t.maxLength ? "messageTemplates__error" : ""}>
                                    {value.length} / {t.maxLength}
                                 </span>
                                 {dirty && error && (
                                    <span className="messageTemplates__error">{error}</span>
                                 )}
                              </div>

                              <div className="messageTemplates__preview">
                                 <span className="messageTemplates__previewLabel">
                                    {t.kind === "url" ? "Butonul deschide:" : "Așa arată (cu valori de exemplu):"}
                                 </span>
                                 <p>{preview}</p>
                              </div>

                              <div className="messageTemplates__actions">
                                 <button
                                    type="button"
                                    className="messageTemplates__btn messageTemplates__btn--primary"
                                    disabled={!dirty || Boolean(error) || busy}
                                    onClick={() => save(t)}
                                 >
                                    {busy ? "Se salvează..." : "Salvează"}
                                 </button>
                                 {dirty && (
                                    <button
                                       type="button"
                                       className="messageTemplates__btn"
                                       disabled={busy}
                                       onClick={() =>
                                          setDrafts((cur) => {
                                             const next = { ...cur };
                                             delete next[t.key];
                                             return next;
                                          })
                                       }
                                    >
                                       Anulează modificările
                                    </button>
                                 )}
                                 {t.isCustom && (
                                    <button
                                       type="button"
                                       className="messageTemplates__btn"
                                       disabled={busy}
                                       onClick={() => reset(t)}
                                    >
                                       Revino la textul implicit
                                    </button>
                                 )}
                                 {fb?.text && (
                                    <span
                                       className={
                                          fb.ok ? "messageTemplates__ok" : "messageTemplates__error"
                                       }
                                    >
                                       {fb.text}
                                    </span>
                                 )}
                              </div>
                           </article>
                        );
                     })}
                  </div>
               </section>
            ))}
         </section>
      </main>
   );
}
